import React, {useEffect} from "react";
import useState from 'react-usestateref';
import {beep as beepNow, WORKER_TYPE} from "../helpers";
import {CODE_TYPE} from "../transformers/base";
import { ScanLine, BookOpen, Tablet } from 'lucide-react';
import "../css/scan.css";

const BTN_TXT = {
  START: "SCAN",
  STOP: "STOP"
};

const CANVAS_SIZE = {
  WIDTH: 320,
  HEIGHT: 430
};

const CAPTURE_OPTIONS = {
  audio: false,
  video: {facingMode: "environment"}
}

const READ_STATE = {
  0: "READ",
  1: "UNREAD",
  2: "READING"
};

const OWNERSHIP_STATE = {
  0: "OWNED",
  1: "ORDERED",
  2: "WANTED"
};

const sw = CANVAS_SIZE.WIDTH;
const sh = CANVAS_SIZE.HEIGHT;
const dw = sw;
const dh = sh;
const dx = 0;
const dy = 0;
let sx = 0;
let sy = 0;

const crossHairSvg = "M77.125 148.02567c0-3.5774 2.73862-6.27567 6.37076-6.27567H119V117H84.0192C66.50812 117 52 130.77595 52 148.02567V183h25.125v-34.97433zM237.37338 117H202v24.75h35.18494c3.63161 0 6.69006 2.69775 6.69006 6.27567V183H269v-34.97433C269 130.77595 254.88446 117 237.37338 117zM243.875 285.4587c0 3.5774-2.73863 6.27567-6.37076 6.27567H202V317h35.50424C255.01532 317 269 302.70842 269 285.4587V251h-25.125v34.4587zM83.49576 291.73438c-3.63213 0-6.37076-2.69776-6.37076-6.27568V251H52v34.4587C52 302.70842 66.50812 317 84.0192 317H119v-25.26563H83.49576z";
const crossHairWidth = 217, crossHairHeight = 200, x0 = 53, y0 = 117;

export default function Scan({
  beep = true,
  decode = true,
  worker = WORKER_TYPE.WASM,
  scanRate = 250,
  bw = false,
  crosshair = true,
  onBookAdded,
  onError,
}) {

  // Component state
  const [btnText, setBtnText] = useState(BTN_TXT.START);
  const [scanning, setScanning] = useState(false);

  const [bwOn, setBwOn, bwRef] = useState(bw);
  const [crosshairOn, setCrosshairOn, crosshairRef] = useState(crosshair);

  const [transformToggle, setTransformToggle] = useState(true);
  const [rawCode, setRawCode] = useState();
  const [codeType, setCodeType] = useState();
  const [beepOn, setBeepOn] = useState(beep);

  const [video] = useState(document.createElement("video"));
  const [barcode, setBarcode] = useState();
  const [milliseconds, setMilliseconds] = useState();
  const [resultOpen, setResultOpen] = useState();

  const [readState, setReadState] = useState('UNREAD');
  const [ownershipState, setOwnershipState] = useState('OWNED');
  const [format, setFormat] = useState(false);

  // Constants
  let qrworker = null;
  let canvasElement = null;
  let canvas = null;
  let oldTime = 0;

  video.onplaying = () => {
    sx = (video.videoWidth - CANVAS_SIZE.WIDTH) / 2;
    sy = (video.videoHeight - CANVAS_SIZE.HEIGHT) / 2;
  };

  const initWorker = () => {
    qrworker = new Worker(worker + "Worker.js");
    qrworker.onmessage = async ev => {
      if (ev.data != null) {
        qrworker.terminate();
        const result = ev.data;
        await stopScan();

        let res = result.data;
        const millis = ev.data.ms;
        const rawCode = res;
        let codeType = CODE_TYPE.RAW;

        setBarcode(res);
        setResultOpen(true);
        setRawCode(rawCode);
        setCodeType(codeType);
        setMilliseconds(millis);
        if (beepOn) beepNow();
        sendDataToServer(res); // Send scanned barcode to the server

      }
    };
  };

  const startScan = async () => {
    initWorker();
    canvasElement = document.getElementById("canvas");
    canvas = canvasElement.getContext("2d", {willReadFrequently: true});

    setBtnText(BTN_TXT.STOP);
    setBarcode(null);
    setResultOpen(false);
    setTransformToggle(true);
    setRawCode(null);
    setCodeType(CODE_TYPE.RAW);

    try {
      video.srcObject = await navigator.mediaDevices.getUserMedia(CAPTURE_OPTIONS);
      video.setAttribute("playsinline", "true");
      await video.play();
      setScanning(true);

      requestAnimationFrame(tick);
    } catch (err) {
      stopScan().then();
      console.log("stopped by the user");
      alert(err);
    }
  };

  const stopScan = async () => {
    setScanning(false);
    setBtnText(BTN_TXT.START);
    await video.pause();
    if (video.srcObject) {
      video.srcObject.getVideoTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
  };

  const tick = (time) => {
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.drawImage(video, sx, sy, sw, sh, dx, dy, dw, dh);

      if (bwRef.current) monochromize();
      if (crosshairRef.current) drawCrosshair();
      if (scanning) requestAnimationFrame(tick);
      if (decode) recogniseQRcode(time);
    }
    requestAnimationFrame(tick);
  };

  const sendDataToServer = async (barcode) => {
    const token = localStorage.getItem('accessToken');

    try {
      const response = await fetch("/api/library/add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`

        },
        body: JSON.stringify({
          isbn: barcode,
          owner: localStorage.getItem('loggedInUsername'),
          readState: readState,
          ownershipState: ownershipState,
          timestamp: Math.floor(Date.now() / 1000),
          format
        })
      });   
      if (response.status == 200) {

        const content = await response.json();
        const imageLinks = JSON.parse(content.thumbnail)

        if (typeof onBookAdded === 'function') {
          onBookAdded({
            title: content.title,
            thumbnail: imageLinks.thumbnail,
            isbn: barcode
          });
        }
      }else{
        const errorR = await response.json();
        console.log(errorR.error);
        if (typeof onError === 'function') {
          onError(errorR.error);
        }
      }

    } catch (error) {
      console.error('There was a problem with your fetch operation:', error);
    }
  };

  // Render option pills
  const renderOptions = () => {
    return (
      <div className="scan-options">
        <div className="scan-option-row">
          <span className="scan-option-label">Status</span>
          <div className="scan-pills">
            {Object.entries(READ_STATE).map(([key, value]) => (
              <button key={key} className={`scan-pill${readState === value ? ' active' : ''}`} onClick={() => setReadState(value)}>{value.charAt(0) + value.slice(1).toLowerCase()}</button>
            ))}
          </div>
        </div>
        <div className="scan-option-row">
          <span className="scan-option-label">Ownership</span>
          <div className="scan-pills">
            {Object.entries(OWNERSHIP_STATE).map(([key, value]) => (
              <button key={key} className={`scan-pill${ownershipState === value ? ' active' : ''}`} onClick={() => setOwnershipState(value)}>{value.charAt(0) + value.slice(1).toLowerCase()}</button>
            ))}
          </div>
        </div>
        <div className="scan-option-row">
          <span className="scan-option-label">Format</span>
          <div className="scan-pills">
            <button className={`scan-pill${!format ? ' active' : ''}`} onClick={() => setFormat(false)}><BookOpen size={12} /> Physical</button>
            <button className={`scan-pill${format ? ' active' : ''}`} onClick={() => setFormat(true)}><Tablet size={12} /> Digital</button>
          </div>
        </div>
      </div>
    );
  };
  
  const monochromize = () => {
    let imgd = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
    let pix = imgd.data;
    for (let i = 0; i < pix.length; i += 4) {
      let gray = pix[i] * 0.3 + pix[i + 1] * 0.59 + pix[i + 2] * 0.11;
      pix[i] = gray;
      pix[i + 1] = gray;
      pix[i + 2] = gray;
    }
    canvas.putImageData(imgd, 0, 0);
  };

  const drawCrosshair = () => {
    canvas.fillStyle = "rgba(255,255,255,0.4)";
    const shape = new Path2D(crossHairSvg);
    canvas.fill(shape);
  };

  const recogniseQRcode = (time) => {
    if (time - oldTime > scanRate) {
      oldTime = time;
      let imageData;
      if (crosshairRef.current === true)
        imageData = canvas.getImageData(x0, y0, crossHairWidth, crossHairHeight);
      else
        imageData = canvas.getImageData(0, 0, canvasElement.width, canvasElement.height);
      qrworker.postMessage({width: imageData.width, height: imageData.height});
      qrworker.postMessage(imageData, [imageData.data.buffer]);
    }
  };

  const onBtnClickHandler = async (e) => {
    e.preventDefault();
    if (scanning) await stopScan(); else await startScan();
  };

  const onCrossHairClickHandler = async (e) => {
    e.preventDefault();
    setCrosshairOn(!crosshairOn);
  };

  const onBWClickHandler = async (e) => {
    e.preventDefault();
    setBwOn(!bwOn);
  };

  const onBeepClickHandler = async (e) => {
    e.preventDefault();
    setBeepOn(!beepOn);
  };

  useEffect(() => {}, []);

  return (
    <div className="scan-wrapper">
      {/* Camera Viewfinder */}
      <div className="scan-viewfinder">
        <canvas id="canvas" className="scanCanvas" width={CANVAS_SIZE.WIDTH} height={CANVAS_SIZE.HEIGHT} />
        {!scanning && (
          <div className="scan-idle-overlay">
            <ScanLine size={48} />
            <p>Point your camera at a barcode</p>
          </div>
        )}
      </div>

      {/* Scan / Stop Button */}
      <button
        type="button"
        className={`scan-action-btn ${scanning ? 'stop' : 'start'}`}
        onClick={onBtnClickHandler}
      >
        <ScanLine size={16} />
        {scanning ? 'Stop Scanning' : 'Start Scanning'}
      </button>

      {/* Option pills */}
      {renderOptions()}
    </div>
  )
};
